import { BUSINESS_CENTRAL_SCOPES } from "@/msal-config"
import {
  AuthenticationResult,
  InteractionRequiredAuthError,
} from "@azure/msal-browser"
import { useMsal } from "@azure/msal-react"
import { useCallback } from "react"

// MSAL needs window.crypto.subtle (secure contexts only) — mirrors SharePoint.
export const isMsalAvailable = !!window.crypto?.subtle

/**
 * Microsoft sign-in for Business Central (delegated). Reuses the same MSAL app as
 * SharePoint; only the scope differs — BC tokens are audience-specific, so they're
 * acquired separately from Graph tokens. `signIn` opens the consent popup and
 * returns the account + token; `getToken` fetches a fresh token silently (popup
 * only if interaction is required) for each preview/sync.
 */
const useErpMicrosoftAuthEnabled = () => {
  const { instance, accounts } = useMsal()

  const signIn = useCallback(
    (): Promise<AuthenticationResult> =>
      instance.loginPopup({
        scopes: BUSINESS_CENTRAL_SCOPES,
        prompt: "select_account",
      }),
    [instance],
  )

  const getToken = useCallback(async (): Promise<string> => {
    const account = accounts[0]
    if (account) {
      try {
        const res = await instance.acquireTokenSilent({
          scopes: BUSINESS_CENTRAL_SCOPES,
          account,
        })
        return res.accessToken
      } catch (err) {
        if (!(err instanceof InteractionRequiredAuthError)) throw err
      }
    }
    const res = await instance.acquireTokenPopup({
      scopes: BUSINESS_CENTRAL_SCOPES,
    })
    return res.accessToken
  }, [instance, accounts])

  return { isMsalAvailable, signIn, getToken }
}

/**
 * No-op version used when MsalProvider is absent (non-secure context). `index.tsx`
 * only mounts `<MsalProvider>` when `isMsalAvailable` — `useMsal()` throws
 * synchronously during render with no provider ancestor, and `ErpImportPanel` calls
 * this hook unconditionally (every ERP card, not just Business Central's), so
 * without this split an org on http/non-localhost crashes the whole Import-from-ERP
 * tab the moment it renders, before the panel's own `isMsalAvailable` button-guard
 * ever gets a chance to run. Same shape as `useSharepointBrowse`'s enabled/disabled
 * split — keeps hook call-order stable, just never touches `useMsal()`.
 */
const useErpMicrosoftAuthDisabled = () => {
  const signIn = useCallback((): Promise<AuthenticationResult> => {
    throw new Error("Microsoft sign-in requires a secure (https) context.")
  }, [])
  const getToken = useCallback((): Promise<string> => {
    throw new Error("Microsoft sign-in requires a secure (https) context.")
  }, [])
  return { isMsalAvailable, signIn, getToken }
}

// Module-level selection: chosen once at load time, so hooks call-order is stable.
export const useErpMicrosoftAuth = isMsalAvailable
  ? useErpMicrosoftAuthEnabled
  : useErpMicrosoftAuthDisabled
